import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import AccessDenied from "../../common/AccessDenied";
import Loader from "../../common/Loader";
import Toast from "../../common/Toast";
import BetwayAccountFragment from "../../fragments/BetwayAccountFragment";
import PredictionsFragment from "../../fragments/PredictionsFragment";
import PrizesFragment from "../../fragments/PrizesFragment";
import RankingFragment from "../../fragments/RankingFragment";
import RulesFragment from "../../fragments/RulesFragment";
import TermsFragment from "../../fragments/TermsFragment";
import useFansUnitedSDK from "../../hooks/useFansUnitedSDK";
import { useToast } from "../../hooks/useToast";
import TabsNavigation from "./components/TabsNavigation";

import { authenticateWithToken, getUserRequest, setShowOddsInMatchCard } from "../../redux/user";
import { setAuthError, setAuthenticated, setAuthPending } from "../../redux/user/reducer";
import { getUserToken, getAuthenticated, getAuthError, getAuthPending } from "../../redux/user/selectors";

const MainScreen = () => {
	const [selectedTab, setSelectedTab] = useState(0);
	const [toastMessage, setToastMessage] = useState(null);
	const [searchParams] = useSearchParams();

	const { i18n } = useTranslation();

	const userToken = useSelector(getUserToken);
	const authenticated = useSelector(getAuthenticated);
	const authError = useSelector(getAuthError);
	const authPending = useSelector(getAuthPending);

	const fansUnitedSDK = useFansUnitedSDK();
	const showToast = useToast(() => setToastMessage(null), toastMessage);

	const dispatch = useDispatch();

	useEffect(() => {
		const token = searchParams.get("token");
		const country = searchParams.get("country");
		const language = searchParams.get("language") || "en";
		const showOddsParam = searchParams.get("show_odds_in_match_card");

		if (!token) {
			setToastMessage({ text: "Invalid JWT token is provided", type: "error" });
			// Set authentication state to failed when no token is provided
			dispatch(setAuthError("Invalid JWT token is provided"));
			dispatch(setAuthenticated(false));
			dispatch(setAuthPending(false));
			return;
		}

		dispatch(authenticateWithToken(token, country, language));

		// Handle show_odds_in_match_card parameter
		const showOdds = showOddsParam === "true" || showOddsParam === "1";
		dispatch(setShowOddsInMatchCard(showOdds));

		if (language !== "en") {
			i18n.changeLanguage(language);
			return;
		}

		if (country && ["es", "mx", "pa"].includes(country.toLowerCase())) {
			i18n.changeLanguage("es");
		}
	}, [searchParams, dispatch, i18n]);

	useEffect(() => {
		if (!userToken || !fansUnitedSDK) return;

		dispatch(getUserRequest(fansUnitedSDK));
	}, [userToken, fansUnitedSDK, dispatch]);

	// Show toast error when authentication fails
	useEffect(() => {
		if (authError) {
			setToastMessage({ text: authError, type: "error" });
		}
	}, [authError]);

	const goToPrizes = () => {
		setSelectedTab(2);
	};

	const goToRules = () => {
		setSelectedTab(4);
	};

	// Show loading while authentication is pending
	if (authPending) {
		return <Loader />;
	}

	// Show access denied for any authentication failure or missing token
	if (authError || !userToken || (userToken && !authenticated)) {
		return (
			<>
				<AccessDenied />
			</>
		);
	}

	const screens = [
		<PredictionsFragment goToPrizes={goToPrizes} />,
		<RankingFragment />,
		<PrizesFragment goToRules={goToRules} />,
		<BetwayAccountFragment />,
		<RulesFragment />,
		<TermsFragment />,
		// <LeaguesFragment />,
		// <BadgesFragment />,
	];

	return (
		<div>
			<TabsNavigation selectedTab={selectedTab} onTabSelect={setSelectedTab} />
			<div style={{ marginTop: "60px" }}>{screens[selectedTab]}</div>
			{showToast && <Toast message={toastMessage} />}
		</div>
	);
};

export default MainScreen;
